import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Alert,
    Avatar,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Empty,
    Form,
    Input,
    Modal,
    Pagination,
    Popconfirm,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from "antd";

import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    CopyOutlined,
    EyeOutlined,
    IdcardOutlined,
    PrinterOutlined,
    QrcodeOutlined,
    ReloadOutlined,
    SearchOutlined,
    StopOutlined,
    SyncOutlined,
    UserAddOutlined,
} from "@ant-design/icons";

import { QRCodeCanvas } from "qrcode.react";
import TeacherAttendancePDF from "./TeacherAttendancePDF";
import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";


const {
    Title,
    Text,
} = Typography;


/*
|--------------------------------------------------------------------------
| API ENDPOINTS
|--------------------------------------------------------------------------
*/

const CARDS_ENDPOINT =
    "/api/smart-teacher-attendance/cards/";

const TEACHERS_ENDPOINT =
    "teachers-listing/";

const REVOKE_ENDPOINT =
    (id) =>
        `/api/smart-teacher-attendance/cards/${id}/revoke/`;

const REPLACE_ENDPOINT =
    (id) =>
        `/api/smart-teacher-attendance/cards/${id}/replace/`;


/*
|--------------------------------------------------------------------------
| CARD STATUS
|--------------------------------------------------------------------------
*/

const CARD_STATUS = {
    ACTIVE: "active",
    REVOKED: "revoked",
    REPLACED: "replaced",
    EXPIRED: "expired",
};


/*
|--------------------------------------------------------------------------
| PAGE SIZE
|--------------------------------------------------------------------------
*/

const PAGE_SIZE = 10;


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const TeacherSmartCardManagement = () => {

    const {
        selectedYear,
    } = useAcademicYear();


    // =====================================================
    // STATE
    // =====================================================

    const [cards, setCards] = useState([]);

    const [teachers, setTeachers] = useState([]);

    const [loading, setLoading] =
        useState(false);

    const [teachersLoading, setTeachersLoading] =
        useState(false);

    const [submitting, setSubmitting] =
        useState(false);

    const [searchText, setSearchText] =
        useState("");

    const [statusFilter, setStatusFilter] =
        useState("all");

    const [page, setPage] =
        useState(1);

    const [total, setTotal] =
        useState(0);

    const [error, setError] =
        useState(null);


    /*
    |--------------------------------------------------------------------------
    | MODALS
    |--------------------------------------------------------------------------
    */

    const [issueModalOpen, setIssueModalOpen] =
        useState(false);

    const [viewModalOpen, setViewModalOpen] =
        useState(false);

    const [replaceModalOpen, setReplaceModalOpen] =
        useState(false);


    /*
    |--------------------------------------------------------------------------
    | SELECTED CARD
    |--------------------------------------------------------------------------
    */

    const [selectedCard, setSelectedCard] =
        useState(null);


    /*
    |--------------------------------------------------------------------------
    | FORMS
    |--------------------------------------------------------------------------
    */

    const [issueForm] =
        Form.useForm();

    const [replaceForm] =
        Form.useForm();


    // =====================================================
    // FETCH CARDS
    // =====================================================

    const fetchCards = useCallback(
        async () => {

            if (!selectedYear?.id) {

                setCards([]);
                setTotal(0);

                return;
            }


            setLoading(true);
            setError(null);


            try {

                const params = {
                    page,
                    page_size: PAGE_SIZE,
                };


                /*
                |--------------------------------------------------------------------------
                | Only send academic_year if your backend
                | TeacherSmartCardViewSet supports it.
                |--------------------------------------------------------------------------
                */

                params.academic_year =
                    selectedYear.id;


                if (
                    searchText.trim()
                ) {

                    params.search =
                        searchText.trim();

                }


                if (
                    statusFilter !== "all"
                ) {

                    params.status =
                        statusFilter;

                }


                const response =
                    await api.get(
                        CARDS_ENDPOINT,
                        {
                            params,
                        }
                    );


                const data =
                    response.data || {};


                /*
                |--------------------------------------------------------------------------
                | DRF PAGINATION
                |--------------------------------------------------------------------------
                */

                if (
                    Array.isArray(
                        data.results
                    )
                ) {

                    setCards(
                        data.results
                    );

                    setTotal(
                        data.count || 0
                    );

                } else if (
                    Array.isArray(data)
                ) {

                    setCards(data);

                    setTotal(
                        data.length
                    );

                } else {

                    setCards([]);

                    setTotal(0);

                }

            } catch (requestError) {

                console.error(
                    "Failed to load teacher smart cards:",
                    requestError
                );


                setError(
                    requestError
                        ?.response
                        ?.data
                        ?.detail ||
                    requestError
                        ?.response
                        ?.data
                        ?.message ||
                    "Unable to load teacher Smart Cards."
                );

            } finally {

                setLoading(false);

            }

        },
        [
            selectedYear?.id,
            page,
            searchText,
            statusFilter,
        ]
    );


    // =====================================================
    // FETCH TEACHERS
    // =====================================================

    const fetchTeachers = useCallback(
        async () => {

            if (!selectedYear?.id) {

                setTeachers([]);

                return;
            }


            setTeachersLoading(true);


            try {

                const response =
                    await api.get(
                        TEACHERS_ENDPOINT,
                        {
                            params: {
                                academic_year:
                                    selectedYear.id,

                                page_size:
                                    1000,
                            },
                        }
                    );


                const data =
                    response.data || {};


                if (
                    Array.isArray(
                        data.results
                    )
                ) {

                    setTeachers(
                        data.results
                    );

                } else if (
                    Array.isArray(data)
                ) {

                    setTeachers(data);

                } else {

                    setTeachers([]);

                }

            } catch (requestError) {

                console.error(
                    "Failed to load teachers:",
                    requestError
                );


                message.error(
                    "Unable to load teachers."
                );

            } finally {

                setTeachersLoading(false);

            }

        },
        [
            selectedYear?.id,
        ]
    );


    // =====================================================
    // INITIAL LOAD
    // =====================================================

    useEffect(() => {

        fetchCards();

    }, [
        fetchCards,
    ]);


    // =====================================================
    // LOAD TEACHERS WHEN ISSUE MODAL OPENS
    // =====================================================

    useEffect(() => {

        if (
            issueModalOpen
        ) {

            fetchTeachers();

        }

    }, [
        issueModalOpen,
        fetchTeachers,
    ]);


    // =====================================================
    // RESET PAGE WHEN FILTERS CHANGE
    // =====================================================

    useEffect(() => {

        setPage(1);

    }, [
        searchText,
        statusFilter,
        selectedYear?.id,
    ]);


    // =====================================================
    // STATISTICS
    // =====================================================

    const statistics =
        useMemo(() => {

            const active =
                cards.filter(
                    card =>
                        card.status ===
                        CARD_STATUS.ACTIVE
                ).length;


            const revoked =
                cards.filter(
                    card =>
                        card.status ===
                        CARD_STATUS.REVOKED
                ).length;


            const replaced =
                cards.filter(
                    card =>
                        card.status ===
                        CARD_STATUS.REPLACED
                ).length;


            const expired =
                cards.filter(
                    card =>
                        card.status ===
                        CARD_STATUS.EXPIRED
                ).length;


            return {
                active,
                revoked,
                replaced,
                expired,
            };

        }, [
            cards,
        ]);


    // =====================================================
    // OPEN ISSUE MODAL
    // =====================================================

    const openIssueModal = () => {

        if (!selectedYear?.id) {

            message.warning(
                "There is no active academic year."
            );

            return;
        }


        issueForm.resetFields();

        setIssueModalOpen(true);

    };


    // =====================================================
    // ISSUE CARD
    // =====================================================

    const handleIssueCard =
        async () => {

            try {

                const values =
                    await issueForm.validateFields();


                if (!selectedYear?.id) {

                    message.error(
                        "No active academic year."
                    );

                    return;
                }


                setSubmitting(true);


                /*
                |--------------------------------------------------------------------------
                | IMPORTANT
                |--------------------------------------------------------------------------
                |
                | DO NOT SEND card_number.
                |
                | Django generates it automatically.
                |
                | Example:
                |
                | TSC-2026-000001
                | TSC-2026-000002
                | TSC-2026-000003
                |
                |--------------------------------------------------------------------------
                */

                const payload = {

                    teacher:
                        values.teacher,

                    academic_year:
                        selectedYear.id,

                };


                const response =
                    await api.post(
                        CARDS_ENDPOINT,
                        payload
                    );


                /*
                |--------------------------------------------------------------------------
                | POST /cards/ returns the newly-created
                | TeacherSmartCard directly.
                |--------------------------------------------------------------------------
                */

                const createdCard =
                    response.data;


                if (
                    !createdCard
                ) {

                    throw new Error(
                        "The server did not return the created Smart Card."
                    );

                }


                message.success(
                    `Smart Card ${
                        createdCard.card_number ||
                        ""
                    } issued successfully.`
                );


                setIssueModalOpen(
                    false
                );


                issueForm.resetFields();


                await fetchCards();


                /*
                |--------------------------------------------------------------------------
                | Immediately show newly created card.
                |--------------------------------------------------------------------------
                */

                setSelectedCard(
                    createdCard
                );

                setViewModalOpen(
                    true
                );

            } catch (requestError) {

                /*
                |--------------------------------------------------------------------------
                | Ant Design validation error
                |--------------------------------------------------------------------------
                */

                if (
                    requestError?.errorFields
                ) {

                    return;

                }


                console.error(
                    "Failed to issue Smart Card:",
                    requestError
                );


                const errorData =
                    requestError
                        ?.response
                        ?.data;


                message.error(
                    errorData?.detail ||
                    errorData?.message ||
                    errorData?.teacher?.[0] ||
                    errorData?.card_number?.[0] ||
                    "Failed to issue Smart Card."
                );

            } finally {

                setSubmitting(false);

            }

        };


    // =====================================================
    // VIEW CARD
    // =====================================================

    const handleViewCard = (
        card
    ) => {

        setSelectedCard(
            card
        );

        setViewModalOpen(
            true
        );

    };


    // =====================================================
    // REVOKE CARD
    // =====================================================

    const handleRevokeCard =
        async (
            card
        ) => {

            try {

                setSubmitting(true);


                await api.post(
                    REVOKE_ENDPOINT(
                        card.id
                    ),
                    {
                        reason:
                            "Card revoked by administrator.",
                    }
                );


                message.success(
                    "Smart Card revoked successfully."
                );


                await fetchCards();


                /*
                |--------------------------------------------------------------------------
                | Close the view modal after revocation.
                |--------------------------------------------------------------------------
                */

                if (
                    selectedCard?.id ===
                    card.id
                ) {

                    setViewModalOpen(
                        false
                    );

                    setSelectedCard(
                        null
                    );

                }

            } catch (requestError) {

                console.error(
                    "Failed to revoke card:",
                    requestError
                );


                message.error(
                    requestError
                        ?.response
                        ?.data
                        ?.detail ||
                    requestError
                        ?.response
                        ?.data
                        ?.message ||
                    "Unable to revoke Smart Card."
                );

            } finally {

                setSubmitting(false);

            }

        };


    // =====================================================
    // OPEN REPLACE MODAL
    // =====================================================

    const openReplaceModal = (
        card
    ) => {

        setSelectedCard(
            card
        );

        replaceForm.resetFields();

        setReplaceModalOpen(
            true
        );

    };


    // =====================================================
    // REPLACE CARD
    // =====================================================

    const handleReplaceCard =
        async () => {

            try {

                const values =
                    await replaceForm.validateFields();


                if (
                    !selectedCard
                ) {

                    message.error(
                        "No Smart Card selected."
                    );

                    return;

                }


                setSubmitting(true);


                const payload = {

                    reason:
                        values.reason ||
                        "Card replaced.",

                };


                const response =
                    await api.post(
                        REPLACE_ENDPOINT(
                            selectedCard.id
                        ),
                        payload
                    );


                /*
                |--------------------------------------------------------------------------
                | IMPORTANT
                |--------------------------------------------------------------------------
                |
                | Your Django replace endpoint returns:
                |
                | {
                |     success: true,
                |     message: "...",
                |     new_card: {...}
                | }
                |
                | Therefore we MUST use response.data.new_card.
                |--------------------------------------------------------------------------
                */

                const newCard =
                    response.data?.new_card;


                if (
                    !newCard
                ) {

                    throw new Error(
                        "The server did not return the replacement Smart Card."
                    );

                }


                message.success(
                    `New Smart Card ${
                        newCard.card_number ||
                        ""
                    } generated successfully.`
                );


                setReplaceModalOpen(
                    false
                );


                replaceForm.resetFields();


                await fetchCards();


                /*
                |--------------------------------------------------------------------------
                | Display replacement card immediately.
                |--------------------------------------------------------------------------
                */

                setSelectedCard(
                    newCard
                );

                setViewModalOpen(
                    true
                );

            } catch (requestError) {

                if (
                    requestError?.errorFields
                ) {

                    return;

                }


                console.error(
                    "Failed to replace card:",
                    requestError
                );


                const errorData =
                    requestError
                        ?.response
                        ?.data;


                message.error(
                    errorData?.detail ||
                    errorData?.message ||
                    errorData?.reason?.[0] ||
                    "Unable to replace Smart Card."
                );

            } finally {

                setSubmitting(false);

            }

        };


    // =====================================================
    // COPY QR TOKEN
    // =====================================================

    const copyQrToken =
        async (
            token
        ) => {

            if (!token) {

                return;

            }


            try {

                await navigator
                    .clipboard
                    .writeText(
                        token
                    );


                message.success(
                    "QR token copied."
                );

            } catch (error) {

                console.error(
                    "Copy QR token failed:",
                    error
                );


                message.error(
                    "Unable to copy QR token."
                );

            }

        };





    // =====================================================
    // STATUS RENDERER
    // =====================================================

    const renderStatus = (
        status
    ) => {

        switch (
            status
        ) {

            case CARD_STATUS.ACTIVE:

                return (
                    <Tag
                        icon={
                            <CheckCircleOutlined />
                        }
                        color="success"
                    >
                        Active
                    </Tag>
                );


            case CARD_STATUS.REVOKED:

                return (
                    <Tag
                        icon={
                            <StopOutlined />
                        }
                        color="error"
                    >
                        Revoked
                    </Tag>
                );


            case CARD_STATUS.REPLACED:

                return (
                    <Tag
                        icon={
                            <SyncOutlined />
                        }
                        color="warning"
                    >
                        Replaced
                    </Tag>
                );


            case CARD_STATUS.EXPIRED:

                return (
                    <Tag
                        icon={
                            <CloseCircleOutlined />
                        }
                        color="default"
                    >
                        Expired
                    </Tag>
                );


            default:

                return (
                    <Tag>
                        {status || "Unknown"}
                    </Tag>
                );

        }

    };


    // =====================================================
    // TEACHER NAME
    // =====================================================

    const getTeacherName = (
        card
    ) => {

        return (
            card.teacher?.full_name ||
            card.teacher_name ||
            card.teacher?.name ||
            "Unknown Teacher"
        );

    };


    // =====================================================
    // TEACHER USERNAME
    // =====================================================

    const getTeacherUsername = (
        card
    ) => {

        return (
            card.teacher?.username ||
            card.teacher_username ||
            card.username ||
            "--"
        );

    };


    // =====================================================
    // TABLE COLUMNS
    // =====================================================

    const columns = [

        {
            title: "Teacher",

            key: "teacher",

            render: (_, card) => (

                <Space>

                    <Avatar
                        style={{
                            background:
                                "#f97316",
                        }}
                    >
                        {getTeacherName(
                            card
                        )
                            .charAt(0)
                            .toUpperCase()}
                    </Avatar>


                    <div>

                        <Text
                            strong
                        >
                            {getTeacherName(
                                card
                            )}
                        </Text>


                        <br />


                        <Text
                            type="secondary"
                            style={{
                                fontSize: 12,
                            }}
                        >
                            {getTeacherUsername(
                                card
                            )}
                        </Text>

                    </div>

                </Space>

            ),
        },


        {
            title: "Card Number",

            dataIndex:
                "card_number",

            key:
                "card_number",

            render: value => (

                <Text
                    code
                    copyable={
                        Boolean(value)
                    }
                >
                    {value || "--"}
                </Text>

            ),
        },


        {
            title: "Status",

            dataIndex:
                "status",

            key:
                "status",

            render:
                renderStatus,
        },


        {
            title: "Issued",

            dataIndex:
                "issued_at",

            key:
                "issued_at",

            render: value => {

                if (!value) {

                    return "--";

                }


                return new Date(
                    value
                ).toLocaleDateString();

            },
        },


        {
            title: "Actions",

            key: "actions",

            fixed: "right",

            width: 220,

            render: (_, card) => (

                <Space
                    wrap
                >

                    <Tooltip
                        title="View Card"
                    >

                        <Button
                            icon={
                                <EyeOutlined />
                            }
                            onClick={() =>
                                handleViewCard(
                                    card
                                )
                            }
                        />

                    </Tooltip>


 {card.status ===
    CARD_STATUS.ACTIVE && (

    <Tooltip
        title="Download Teacher Card PDF"
    >

        <TeacherAttendancePDF
            academicYear={
                selectedYear
            }

            card={
                card
            }

            buttonText=""

            icon={
                <PrinterOutlined />
            }

            buttonStyle={{
                width: 32,
                height: 32,
                padding: 0,
                border:
                    "1px solid #d9d9d9",
                borderRadius: 6,
                background:
                    "#ffffff",
                color:
                    "#111827",
                cursor:
                    "pointer",
            }}
        />

    </Tooltip>

)}


                    {card.status ===
                        CARD_STATUS.ACTIVE && (

                        <Tooltip
                            title="Replace Card"
                        >

                            <Button
                                icon={
                                    <SyncOutlined />
                                }
                                onClick={() =>
                                    openReplaceModal(
                                        card
                                    )
                                }
                            />

                        </Tooltip>

                    )}


                    {card.status ===
                        CARD_STATUS.ACTIVE && (

                        <Popconfirm
                            title="Revoke this Smart Card?"
                            description={
                                "The QR code will immediately stop working."
                            }
                            okText="Revoke"
                            cancelText="Cancel"
                            okButtonProps={{
                                danger: true,
                            }}
                            onConfirm={() =>
                                handleRevokeCard(
                                    card
                                )
                            }
                        >

                            <Tooltip
                                title="Revoke Card"
                            >

                                <Button
                                    danger
                                    icon={
                                        <StopOutlined />
                                    }
                                />

                            </Tooltip>

                        </Popconfirm>

                    )}

                </Space>

            ),
        },

    ];


    // =====================================================
    // NO ACTIVE YEAR
    // =====================================================

    if (!selectedYear) {

        return (

            <div
                style={{
                    padding: 20,
                }}
            >

                <Alert
                    type="warning"
                    showIcon
                    message={
                        "No Active Academic Year"
                    }
                    description={
                        "Teacher Smart Card management is disabled until an active academic year is selected."
                    }
                />

            </div>

        );

    }


    // =====================================================
    // MAIN PAGE
    // =====================================================

    return (

        <div
            style={{
                minHeight:
                    "100vh",

                background:
                    "#f3f4f6",

                padding:
                    "16px",
            }}
        >

            <div
                style={{
                    maxWidth:
                        1400,

                    margin:
                        "0 auto",
                }}
            >

                {/* ================================================= */}
                {/* HEADER */}
                {/* ================================================= */}

                <Card
                    bordered={false}
                    style={{
                        borderRadius: 16,
                        marginBottom: 16,
                    }}
                >

                    <Row
                        gutter={[
                            16,
                            16,
                        ]}
                        align="middle"
                        justify="space-between"
                    >

                        <Col
                            xs={24}
                            md={14}
                        >

                            <Space
                                align="start"
                                size={14}
                            >

                                <div
                                    style={{
                                        width: 52,
                                        height: 52,
                                        borderRadius: 12,
                                        background:
                                            "#f97316",
                                        display:
                                            "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",
                                    }}
                                >

                                    <IdcardOutlined
                                        style={{
                                            color:
                                                "#fff",
                                            fontSize:
                                                28,
                                        }}
                                    />

                                </div>


                                <div>

                                    <Title
                                        level={2}
                                        style={{
                                            margin: 0,
                                            color:
                                                "#111827",
                                        }}
                                    >
                                        Teacher Smart
                                        Cards
                                    </Title>


                                    <Text
                                        type="secondary"
                                    >
                                        Issue and manage
                                        QR-based Teacher
                                        Smart ID Cards.
                                    </Text>

                                </div>

                            </Space>

                        </Col>


                        <Col
                            xs={24}
                            md={10}
                        >

                            <Space
                                wrap
                                style={{
                                    width: "100%",
                                    justifyContent:
                                        "flex-end",
                                }}
                            >

                                <Tag
                                    color="orange"
                                    icon={
                                        <IdcardOutlined />
                                    }
                                >
                                    {selectedYear.name}
                                </Tag>


                               <Button
    icon={
        <ReloadOutlined />
    }
    onClick={
        fetchCards
    }
    loading={
        loading
    }
>
    Refresh
</Button>


<TeacherAttendancePDF
    academicYear={
        selectedYear
    }
    buttonText="Print All Teacher Cards"
    icon={
        <PrinterOutlined
            style={{
                marginRight: 7,
            }}
        />
    }
    buttonStyle={{
        background:
            "#111827",

        border:
            "1px solid #111827",

        color:
            "#ffffff",

        borderRadius:
            6,

        padding:
            "4px 15px",

        minHeight:
            40,

        fontWeight:
            600,

        cursor:
            "pointer",
    }}
/>


<Button
    type="primary"
    icon={
        <UserAddOutlined />
    }
    onClick={
        openIssueModal
    }
    style={{
        background:
            "#f97316",
        borderColor:
            "#f97316",
        fontWeight:
            600,
    }}
>
    Issue Smart Card
</Button>

                            </Space>

                        </Col>

                    </Row>

                </Card>


                {/* ================================================= */}
                {/* ERROR */}
                {/* ================================================= */}

                {error && (

                    <Alert
                        type="error"
                        showIcon
                        closable
                        message={
                            error
                        }
                        style={{
                            marginBottom: 16,
                        }}
                        onClose={() =>
                            setError(
                                null
                            )
                        }
                    />

                )}


                {/* ================================================= */}
                {/* STATISTICS */}
                {/* ================================================= */}

                <Row
                    gutter={[
                        16,
                        16,
                    ]}
                    style={{
                        marginBottom: 16,
                    }}
                >

                    <Col
                        xs={12}
                        sm={6}
                    >

                        <Card
                            bordered={false}
                        >

                            <Statistic
                                title="Active"
                                value={
                                    statistics.active
                                }
                                prefix={
                                    <CheckCircleOutlined
                                        style={{
                                            color:
                                                "#52c41a",
                                        }}
                                    />
                                }
                            />

                        </Card>

                    </Col>


                    <Col
                        xs={12}
                        sm={6}
                    >

                        <Card
                            bordered={false}
                        >

                            <Statistic
                                title="Revoked"
                                value={
                                    statistics.revoked
                                }
                                prefix={
                                    <StopOutlined
                                        style={{
                                            color:
                                                "#ff4d4f",
                                        }}
                                    />
                                }
                            />

                        </Card>

                    </Col>


                    <Col
                        xs={12}
                        sm={6}
                    >

                        <Card
                            bordered={false}
                        >

                            <Statistic
                                title="Replaced"
                                value={
                                    statistics.replaced
                                }
                                prefix={
                                    <SyncOutlined
                                        style={{
                                            color:
                                                "#faad14",
                                        }}
                                    />
                                }
                            />

                        </Card>

                    </Col>


                    <Col
                        xs={12}
                        sm={6}
                    >

                        <Card
                            bordered={false}
                        >

                            <Statistic
                                title="Expired"
                                value={
                                    statistics.expired
                                }
                                prefix={
                                    <CloseCircleOutlined />
                                }
                            />

                        </Card>

                    </Col>

                </Row>


                {/* ================================================= */}
                {/* SEARCH / FILTER */}
                {/* ================================================= */}

                <Card
                    bordered={false}
                    style={{
                        marginBottom: 16,
                    }}
                >

                    <Row
                        gutter={[
                            12,
                            12,
                        ]}
                    >

                        <Col
                            xs={24}
                            md={12}
                            lg={10}
                        >

                            <Input
                                allowClear
                                prefix={
                                    <SearchOutlined />
                                }
                                placeholder={
                                    "Search teacher, username or card number..."
                                }
                                value={
                                    searchText
                                }
                                onChange={event =>
                                    setSearchText(
                                        event
                                            .target
                                            .value
                                    )
                                }
                                size="large"
                            />

                        </Col>


                        <Col
                            xs={24}
                            sm={12}
                            md={6}
                            lg={5}
                        >

                            <Select
                                value={
                                    statusFilter
                                }
                                onChange={
                                    setStatusFilter
                                }
                                size="large"
                                style={{
                                    width:
                                        "100%",
                                }}
                            >

                                <Select.Option
                                    value="all"
                                >
                                    All Card Statuses
                                </Select.Option>

                                <Select.Option
                                    value="active"
                                >
                                    Active
                                </Select.Option>

                                <Select.Option
                                    value="revoked"
                                >
                                    Revoked
                                </Select.Option>

                                <Select.Option
                                    value="replaced"
                                >
                                    Replaced
                                </Select.Option>

                                <Select.Option
                                    value="expired"
                                >
                                    Expired
                                </Select.Option>

                            </Select>

                        </Col>


                        <Col
                            xs={24}
                            sm={12}
                            md={6}
                            lg={5}
                        >

                            <Button
                                block
                                size="large"
                                icon={
                                    <ReloadOutlined />
                                }
                                onClick={() => {

                                    setSearchText(
                                        ""
                                    );

                                    setStatusFilter(
                                        "all"
                                    );

                                    setPage(
                                        1
                                    );

                                }}
                            >
                                Reset Filters
                            </Button>

                        </Col>

                    </Row>

                </Card>


                {/* ================================================= */}
                {/* CARD TABLE */}
                {/* ================================================= */}

                <Card
                    bordered={false}
                    style={{
                        borderRadius: 16,
                    }}
                >

                    <Table
                        rowKey="id"
                        columns={
                            columns
                        }
                        dataSource={
                            cards
                        }
                        loading={
                            loading
                        }
                        pagination={
                            false
                        }
                        scroll={{
                            x: 900,
                        }}
                        locale={{
                            emptyText: (
                                <Empty
                                    description={
                                        "No teacher Smart Cards found."
                                    }
                                />
                            ),
                        }}
                    />


                    {total > 0 && (

                        <div
                            style={{
                                display:
                                    "flex",
                                justifyContent:
                                    "flex-end",
                                marginTop:
                                    20,
                            }}
                        >

                            <Pagination
                                current={
                                    page
                                }
                                pageSize={
                                    PAGE_SIZE
                                }
                                total={
                                    total
                                }
                                showSizeChanger={
                                    false
                                }
                                showTotal={
                                    value =>
                                        `${value} Smart Cards`
                                }
                                onChange={
                                    setPage
                                }
                            />

                        </div>

                    )}

                </Card>

            </div>


            {/* ===================================================== */}
            {/* ISSUE CARD MODAL */}
            {/* ===================================================== */}

            <Modal
                open={
                    issueModalOpen
                }
                title={
                    <Space>

                        <QrcodeOutlined
                            style={{
                                color:
                                    "#f97316",
                            }}
                        />

                        <span>
                            Issue Teacher Smart Card
                        </span>

                    </Space>
                }
                onCancel={() =>
                    setIssueModalOpen(
                        false
                    )
                }
                onOk={
                    handleIssueCard
                }
                okText="Issue Card"
                confirmLoading={
                    submitting
                }
                okButtonProps={{
                    style: {
                        background:
                            "#f97316",
                        borderColor:
                            "#f97316",
                    },
                }}
                destroyOnHidden
            >

                <Alert
                    type="info"
                    showIcon
                    message="Automatic Smart Card Number"
                    description={
                        "The system will automatically generate a unique card number and secure QR token when the card is issued. No manual card number entry is required."
                    }
                    style={{
                        marginBottom: 20,
                    }}
                />


                <Form
                    form={
                        issueForm
                    }
                    layout="vertical"
                >

                    <Form.Item
                        label="Teacher"
                        name="teacher"
                        rules={[
                            {
                                required:
                                    true,
                                message:
                                    "Please select a teacher.",
                            },
                        ]}
                    >

                        <Select
                            showSearch
                            loading={
                                teachersLoading
                            }
                            placeholder={
                                "Select teacher"
                            }
                            optionFilterProp="label"
                            options={
                                teachers
                                    .filter(
                                        teacher =>
                                            !teacher.has_smart_card &&
                                            !teacher.smart_card
                                    )
                                    .map(
                                        teacher => ({
                                            value:
                                                teacher.id,

                                            label:
                                                teacher.full_name ||
                                                teacher.name ||
                                                teacher.username ||
                                                `Teacher ${teacher.id}`,
                                        })
                                    )
                            }
                        />

                    </Form.Item>


                    <Alert
                        type="warning"
                        showIcon
                        message={
                            `Academic Year: ${selectedYear.name}`
                        }
                    />

                </Form>

            </Modal>


            {/* ===================================================== */}
            {/* VIEW CARD MODAL */}
            {/* ===================================================== */}

            <Modal
                open={
                    viewModalOpen
                }
                title={
                    <Space>

                        <IdcardOutlined
                            style={{
                                color:
                                    "#f97316",
                            }}
                        />

                        <span>
                            Teacher Smart ID Card
                        </span>

                    </Space>
                }
                onCancel={() => {

                    setViewModalOpen(
                        false
                    );

                    setSelectedCard(
                        null
                    );

                }}
                footer={null}
                width={650}
            >

                {selectedCard ? (

                    <div>

                        {/* ========================================= */}
                        {/* QR CARD */}
                        {/* ========================================= */}

                        <div
                            style={{
                                background:
                                    "#111827",
                                borderRadius:
                                    18,
                                padding:
                                    20,
                                color:
                                    "#fff",
                                textAlign:
                                    "center",
                            }}
                        >

                            <Text
                                style={{
                                    color:
                                        "#fff",
                                    fontSize:
                                        18,
                                    fontWeight:
                                        700,
                                }}
                            >
                                TEACHER SMART ID CARD
                            </Text>


                            <div
                                id="teacher-smart-card-qr"
                                style={{
                                    background:
                                        "#fff",
                                    width:
                                        230,
                                    height:
                                        230,
                                    margin:
                                        "22px auto",
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    borderRadius:
                                        12,
                                }}
                            >

                                {selectedCard.qr_token ? (

                                    <QRCodeCanvas
                                        value={
                                            selectedCard.qr_token
                                        }
                                        size={
                                            200
                                        }
                                        level="H"
                                        includeMargin
                                    />

                                ) : (

                                    <Empty
                                        description={
                                            "QR token unavailable"
                                        }
                                    />

                                )}

                            </div>


                            <Title
                                level={3}
                                style={{
                                    color:
                                        "#fff",
                                    margin:
                                        "0 0 5px",
                                }}
                            >
                                {getTeacherName(
                                    selectedCard
                                )}
                            </Title>


                            <Text
                                style={{
                                    color:
                                        "#d1d5db",
                                }}
                            >
                                Teacher Smart
                                Attendance
                            </Text>

                        </div>


                        {/* ========================================= */}
                        {/* DETAILS */}
                        {/* ========================================= */}

                        <Descriptions
                            bordered
                            column={{
                                xs: 1,
                                sm: 2,
                            }}
                            style={{
                                marginTop:
                                    20,
                            }}
                        >

                            <Descriptions.Item
                                label="Teacher"
                            >
                                {getTeacherName(
                                    selectedCard
                                )}
                            </Descriptions.Item>


                            <Descriptions.Item
                                label="Card Number"
                            >

                                <Text
                                    code
                                    copyable={
                                        Boolean(
                                            selectedCard.card_number
                                        )
                                    }
                                >
                                    {
                                        selectedCard.card_number ||
                                        "--"
                                    }
                                </Text>

                            </Descriptions.Item>


                            <Descriptions.Item
                                label="Status"
                            >
                                {renderStatus(
                                    selectedCard.status
                                )}
                            </Descriptions.Item>


                            <Descriptions.Item
                                label="Academic Year"
                            >
                                {
                                    selectedYear.name
                                }
                            </Descriptions.Item>


                            <Descriptions.Item
                                label="Issued"
                            >
                                {
                                    selectedCard.issued_at
                                        ? new Date(
                                            selectedCard.issued_at
                                        ).toLocaleString()
                                        : "--"
                                }
                            </Descriptions.Item>


                            <Descriptions.Item
                                label="QR Token"
                                span={2}
                            >

                                <Space
                                    wrap
                                >

                                    <Text
                                        code
                                        copyable
                                    >
                                        {
                                            selectedCard.qr_token ||
                                            "--"
                                        }
                                    </Text>


                                    {selectedCard.qr_token && (

                                        <Button
                                            size="small"
                                            icon={
                                                <CopyOutlined />
                                            }
                                            onClick={() =>
                                                copyQrToken(
                                                    selectedCard.qr_token
                                                )
                                            }
                                        >
                                            Copy
                                        </Button>

                                    )}

                                </Space>

                            </Descriptions.Item>

                        </Descriptions>


                        {/* ========================================= */}
                        {/* ACTIONS */}
                        {/* ========================================= */}

                        <Divider />


                        <Space
                            wrap
                        >

                               {selectedCard.status ===
                                CARD_STATUS.ACTIVE && (

                                <Button
                                    icon={
                                        <SyncOutlined />
                                    }
                                    onClick={() => {

                                        setViewModalOpen(
                                            false
                                        );

                                        openReplaceModal(
                                            selectedCard
                                        );

                                    }}
                                >
                                    Replace Card
                                </Button>

                            )}


                            {selectedCard.status ===
                                CARD_STATUS.ACTIVE && (

                                <Popconfirm
                                    title={
                                        "Revoke this Smart Card?"
                                    }
                                    description={
                                        "Once revoked, its QR code will no longer be accepted."
                                    }
                                    okText="Revoke"
                                    cancelText="Cancel"
                                    okButtonProps={{
                                        danger:
                                            true,
                                    }}
                                    onConfirm={() =>
                                        handleRevokeCard(
                                            selectedCard
                                        )
                                    }
                                >

                                    <Button
                                        danger
                                        icon={
                                            <StopOutlined />
                                        }
                                        loading={
                                            submitting
                                        }
                                    >
                                        Revoke Card
                                    </Button>

                                </Popconfirm>

                            )}

                        </Space>

                    </div>

                ) : (

                    <Spin />

                )}

            </Modal>


            {/* ===================================================== */}
            {/* REPLACE CARD MODAL */}
            {/* ===================================================== */}

            <Modal
                open={
                    replaceModalOpen
                }
                title={
                    <Space>

                        <SyncOutlined
                            style={{
                                color:
                                    "#f97316",
                            }}
                        />

                        <span>
                            Replace Smart Card
                        </span>

                    </Space>
                }
                onCancel={() =>
                    setReplaceModalOpen(
                        false
                    )
                }
                onOk={
                    handleReplaceCard
                }
                okText="Replace Card"
                confirmLoading={
                    submitting
                }
                okButtonProps={{
                    style: {
                        background:
                            "#f97316",
                        borderColor:
                            "#f97316",
                    },
                }}
            >

                {selectedCard && (

                    <Alert
                        type="warning"
                        showIcon
                        message={
                            `Replacing ${getTeacherName(
                                selectedCard
                            )}'s card`
                        }
                        description={
                            `Current card ${
                                selectedCard.card_number ||
                                ""
                            } will be marked as REPLACED. Django will automatically generate a new card number and a completely new QR token for the replacement card.`
                        }
                        style={{
                            marginBottom:
                                20,
                        }}
                    />

                )}


                <Form
                    form={
                        replaceForm
                    }
                    layout="vertical"
                >

                    <Form.Item
                        label="Reason"
                        name="reason"
                        rules={[
                            {
                                required:
                                    true,

                                message:
                                    "Please provide a replacement reason.",
                            },
                        ]}
                    >

                        <Input.TextArea
                            rows={4}
                            placeholder={
                                "Example: Lost card, damaged card, expired card..."
                            }
                            maxLength={255}
                            showCount
                        />

                    </Form.Item>

                </Form>

            </Modal>


            {/* ===================================================== */}
            {/* MOBILE STYLES */}
            {/* ===================================================== */}

            <style>
                {`

                .ant-table-wrapper {
                    width: 100%;
                }


                .ant-table-cell {
                    white-space: nowrap;
                }


                @media (max-width: 576px) {

                    .ant-card-body {
                        padding: 14px !important;
                    }


                    .ant-statistic-title {
                        font-size: 12px !important;
                    }


                    .ant-statistic-content {
                        font-size: 20px !important;
                    }


                    .ant-modal {
                        max-width:
                            calc(100vw - 20px);
                    }


                    .ant-modal-content {
                        padding: 16px !important;
                    }


                    .ant-descriptions-item-label,
                    .ant-descriptions-item-content {
                        font-size: 13px !important;
                    }

                }

                `}
            </style>

        </div>

    );
};


export default TeacherSmartCardManagement;